import { AlertCircle, Pause, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import type { FlowRunSummary } from "@/types";

interface FlowRecentRunsProps {
  flowId: string;
  runs: FlowRunSummary[];
}

function formatRunStatus(status: string): string {
  switch (status) {
    case "completed":
      return "bg-green-500/10 text-green-400";
    case "failed":
      return "bg-red-500/10 text-red-400";
    case "paused":
      return "bg-yellow-500/10 text-yellow-400";
    case "running":
      return "bg-blue-500/10 text-blue-400";
    case "cancelled":
      return "bg-gray-500/10 text-gray-400";
    default:
      return "bg-surface-secondary text-content-tertiary";
  }
}

export function FlowRecentRuns({ flowId, runs }: Readonly<FlowRecentRunsProps>) {
  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-content-primary">Recent Runs</h2>
        <Link to={`/flows/${flowId}/runs`} className="text-sm text-brand-primary hover:underline">
          View All
        </Link>
      </div>

      {runs.length > 0 ? (
        <div className="space-y-2">
          {runs.map((run) => (
            <Link
              key={run.uuid}
              to={`/flows/${flowId}/runs/${run.uuid}`}
              className={(() => {
                const base = "flex items-center justify-between p-3 rounded-lg transition-colors";
                if (run.status === "paused")
                  return `${base} bg-yellow-500/10 border border-yellow-500/30 hover:bg-yellow-500/20`;
                if (run.status === "failed")
                  return `${base} bg-red-500/10 border border-red-500/30 hover:bg-red-500/20`;
                return `${base} bg-surface-secondary hover:bg-interactive-active`;
              })()}
            >
              <div className="flex items-center gap-3">
                {(() => {
                  if (run.status === "paused") return <Pause className="h-4 w-4 text-yellow-400" />;
                  if (run.status === "failed") return <AlertCircle className="h-4 w-4 text-red-400" />;
                  return <Zap className="h-4 w-4 text-content-tertiary" />;
                })()}
                <div>
                  <div className="text-sm text-content-primary">
                    {new Date(run.createdAt).toLocaleDateString()} {new Date(run.createdAt).toLocaleTimeString()}
                  </div>
                  <div className="text-xs text-content-tertiary mt-0.5">{run.totalTokens.toLocaleString()} tokens</div>
                  {run.status === "failed" && run.errorMessage && (
                    <div className="text-xs text-red-400 mt-0.5 line-clamp-1">{run.errorMessage}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${formatRunStatus(run.status)}`}
                >
                  {run.status}
                </span>
                {run.status === "paused" && <span className="text-xs text-yellow-400">Review Required</span>}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Zap className="h-12 w-12 text-content-quaternary mx-auto mb-3" />
          <p className="text-content-tertiary text-sm">No runs yet. Run this flow to see history.</p>
        </div>
      )}
    </div>
  );
}
